import { FC, useEffect, useState } from 'react';
import { GetServerSideProps, GetServerSidePropsContext } from 'next';
import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';

import initializeApollo, { addApolloState } from 'apollo-client';

import PageMeta, { getMetaInfo } from 'components/layout/PageMeta';
import LayoutConfines from 'components/layout/LayoutConfines';

import MainSlider from 'components/main/MainSlider';
import { ServicesLinks } from 'components/main/ServicesLinks';
import SpeakersOnMain from 'components/main/SpeakersOnMain';
import PostsOnMain from 'components/main/PostsOnMain';

import { useMainSlider, getMainSliderSsr } from 'hooks/main/useMainSlider';
import {
  getPostsWithTotalCountSsr,
  usePostsWithTotalCount,
} from 'hooks/post/usePosts';
import { getSpeakersSsr, useSpeakers } from 'hooks/speaker/useSpeakers';

import { activeExperiments, getExperimentValue } from 'utils/experiments';
import { EStorageKeys } from 'utils/constants';

import { T as DailyQuestionT } from 'components/gamification/DailyQuestion';
import { useAuth } from 'components/context/AuthContext';

import {
  MainSliderType,
  PostType,
  GetSpeakersQueryVariables,
  SpeakerType,
  GetPostsWithTotalCountQueryVariables,
  ContentType,
} from 'graphql/types';
import { IStorageContent } from 'components/library/ContentCardNew/ContentCardNew.types';

const DailyQuestion = dynamic(
  () =>
    import('components/gamification/DailyQuestion').then(
      (c) => c.DailyQuestion
    ) as any
) as FC<DailyQuestionT.IDailyQuestion>;
const CollectionSlider = dynamic(
  () => import('components/library/CollectionSlider')
);

const POSTS_LIMIT = 3;
const SPEAKERS_LIMIT = 30;

const defaultPostsFilters: GetPostsWithTotalCountQueryVariables = {
  offset: 0,
  limit: POSTS_LIMIT,
  published: true,
};

const defaultSpeakersFilters: GetSpeakersQueryVariables = {
  offset: 0,
  limit: SPEAKERS_LIMIT,
  showOnMainPage: true,
};

export default function Home() {
  const { pathname } = useRouter();
  const { isAuthorized } = useAuth();

  const { data: sliderData } = useMainSlider();
  const { data: speakersData } = useSpeakers(defaultSpeakersFilters);
  const { data: postsData } = usePostsWithTotalCount(defaultPostsFilters);

  const [storageContent, setStorageContent] = useState<IStorageContent[]>([]);
  const [isAlreadyWatchedSliderShown, setIsAlreadyWatchedSliderShown] =
    useState(false);

  const slides = sliderData?.mainSlider
    ? (sliderData.mainSlider as MainSliderType[])
    : [];
  const speakersList = (speakersData?.speakers as SpeakerType[]) || [];
  const postsList = (postsData?.postsWithMeta?.results as PostType[]) || [];

  useEffect(() => {
    const storageValue = localStorage.getItem(EStorageKeys.ALREADY_WATCHED);

    if (storageValue) {
      setStorageContent(JSON.parse(storageValue));
    }

    setIsAlreadyWatchedSliderShown(
      !!activeExperiments.find(
        (exp) => exp.value === getExperimentValue(activeExperiments[1].group)
      ) || false
    );
  }, []);

  const handleRemoveSlide = (id: string) => {
    const storageValue = localStorage.getItem(EStorageKeys.ALREADY_WATCHED);

    if (storageValue) {
      const updatedValue = JSON.parse(storageValue).filter(
        (item: ContentType) => item.id !== id
      );
      localStorage.setItem(
        EStorageKeys.ALREADY_WATCHED,
        JSON.stringify(updatedValue)
      );
      setStorageContent(updatedValue);
    }
  };

  return (
    <>
      <PageMeta {...getMetaInfo(pathname)} />
      {slides.length > 0 && <MainSlider data={slides} />}
      <LayoutConfines>
        {isAuthorized &&
          storageContent.length > 0 &&
          isAlreadyWatchedSliderShown && (
            <CollectionSlider
              collections={storageContent as IStorageContent[]}
              collectionName="Вы смотрели"
              collectionType="collection"
              isClosedBtnShown
              onRemoveSlide={handleRemoveSlide}
            />
          )}
      </LayoutConfines>
      <ServicesLinks />
      <DailyQuestion />
      {speakersList.length > 0 && <SpeakersOnMain speakers={speakersList} />}
      {postsList.length > 0 && <PostsOnMain posts={postsList} />}
    </>
  );
}

export const getServerSideProps: GetServerSideProps = async (
  ctx: GetServerSidePropsContext
) => {
  const apolloClient = initializeApollo({ ctx });

  try {
    await Promise.all([
      getMainSliderSsr(apolloClient),
      getSpeakersSsr(apolloClient, defaultSpeakersFilters),
      getPostsWithTotalCountSsr(apolloClient, defaultPostsFilters),
    ]);
  } catch (err) {
    console.log(err);
  }

  return addApolloState(apolloClient, {
    props: {},
  });
};
